import {useCallback} from 'react';
import {create} from 'zustand';
import {useShallow} from 'zustand/react/shallow';
import {immer} from 'zustand/middleware/immer';
import {createJSONStorage, devtools, persist} from 'zustand/middleware';

interface BookExperienceState {
  itemQuantities: {
    [offerId: string]: {
      [itemId: string]: number;
    };
  };
  email: string;
  emailReservationId: string | null;
}

interface BookExperienceActions {
  setEmail: (email: string, reservationId?: string | null) => void;
  setItemQuantity: (payload: {offerId: string; itemId: string; quantity: number}) => void;
  resetItemQuantities: (offerId: string) => void;
}

interface BookExperienceStore extends BookExperienceState, BookExperienceActions {}

const INITIAL_STATE: BookExperienceState = {
  itemQuantities: {},
  email: '',
  emailReservationId: null,
};

export const useBookExperienceStore = create<BookExperienceStore>()(
  devtools(
    persist(
      immer<BookExperienceStore>(set => ({
        ...INITIAL_STATE,

        setEmail: (email, reservationId = null) =>
          set(state => {
            state.email = email;
            state.emailReservationId = reservationId;
          }),

        setItemQuantity: ({offerId, itemId, quantity}) =>
          set(state => {
            if (!state.itemQuantities[offerId]) {
              state.itemQuantities[offerId] = {};
            }
            state.itemQuantities[offerId][itemId] = quantity;
          }),

        resetItemQuantities: offerId =>
          set(state => {
            state.itemQuantities[offerId] = {};
          }),
      })),
      {
        name: 'chekin-book-experience',
        storage: createJSONStorage(() => sessionStorage),
        partialize: state => ({
          email: state.email,
          emailReservationId: state.emailReservationId,
        }),
      },
    ),
    {name: 'BookExperienceStore'},
  ),
);

export const selectEmail = (state: BookExperienceStore) => state.email;
export const selectEmailReservationId = (state: BookExperienceStore) =>
  state.emailReservationId;

export const useItemQuantitiesByOfferId = (offerId?: string) => {
  const selector = useCallback(
    (state: BookExperienceStore) => (offerId ? state.itemQuantities[offerId] : undefined),
    [offerId],
  );
  return useBookExperienceStore(useShallow(selector)) ?? {};
};
